import { useRef, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

declare global {
  interface Window {
    ScrollSmoother: any;
  }
}
import { Home } from './pages/Home';
import { PerformanceMonitor } from './components/Performance';
// Lazy load pages that aren't needed on initial load
const FAQPage = lazy(() => import('./pages/FAQ/FAQ'));
const TermsConditions = lazy(() => import('./pages/Terms/TermsConditions'));
const Privacy = lazy(() => import('./pages/Privacy/Privacy'));
const Support = lazy(() => import('./pages/Support'));
const About = lazy(() => import('./pages/About/About'));
const Events = lazy(() => import('./pages/Events/Events'));
// Dev-only: form-error states harness. Not loaded in production builds so
// the live site doesn't expose mock-PII testing UI (was triggering Google
// Safe Browsing's deceptive-pages classifier).
const FormTestErrors = import.meta.env.DEV
  ? lazy(() => import('./dev/FormTestErrors'))
  : null;
const Navigator = lazy(() => import('./pages/MaevenNavigator/Navigator'));
const PlatformTerms = lazy(() => import('./pages/PlatformTerms/PlatformTerms'));
const DPA = lazy(() => import('./pages/DPA/DPA'));
const Register = lazy(() => import('./pages/Register/Register'));


import { ErrorBoundary } from 'react-error-boundary';
import Header from './components/Header/Header';
const Sidebar = lazy(() => import('./components/Sidebar'));
import Footer from './components/Footer/Footer';
import GrainOverlay from './components/GrainNoise';
import HideShowWhatPages from './components/SectionIndicator/HideShowWhatPages';
import AnimatedPage from './components/AnimatedPage';
import { HomePageProvider } from './contexts/HomePageContext';
import LetsTalkModal from './components/LetsTalkModal';

// Loading fallback for lazy-loaded routes
const RouteFallback = () => (
  <AnimatedPage>
    <div className="flex items-center justify-center min-h-screen gap-2">
      <div className="loading-dot" style={{ animationDelay: '0s' }}></div>
      <div className="loading-dot" style={{ animationDelay: '0.2s' }}></div>
      <div className="loading-dot" style={{ animationDelay: '0.4s' }}></div>
    </div>
    <style>{`
      @keyframes dotAppear {
        0% { 
          opacity: 0;
        }
        10% { 
          opacity: 1;
        }
        30% { 
          opacity: 1;
        }
        40% { 
          opacity: 0;
        }
        100% { 
          opacity: 0;
        }
      }
      .loading-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: var(--GoldBrand-dr-2);
        opacity: 0;
        animation: dotAppear 1.2s ease-in-out infinite;
      }
    `}</style>
  </AnimatedPage>
);

// Scroll to top on page change
const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    requestAnimationFrame(() => {
      const smoothWrapper = document.getElementById('smooth-wrapper');
      if (smoothWrapper) {
        smoothWrapper.scrollTop = 0;
      }
    });
  }, [location.pathname]);

  return null;
};

function App() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarMounted, setSidebarMounted] = useState(false);
  const main = useRef<HTMLDivElement>(null);
  const [seed] = useState(1);

  useEffect(() => {
    // Defer Sidebar mount until the browser is idle so its chunk + icon
    // chunks stay off the initial critical request chain. The Sidebar is
    // the "Get Started" form — not visible until the user opens it — so
    // there's no UX cost to waiting a few hundred ms past first paint.
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    let idleHandle: number | null = null;
    let timeoutHandle: number | null = null;
    if (typeof w.requestIdleCallback === 'function') {
      idleHandle = w.requestIdleCallback(() => setSidebarMounted(true), { timeout: 2000 });
    } else {
      timeoutHandle = window.setTimeout(() => setSidebarMounted(true), 1000);
    }
    return () => {
      if (idleHandle !== null && typeof w.cancelIdleCallback === 'function') {
        w.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
    };
  }, []);

  const handleGetStarted = useCallback(() => {
    setIsSidebarOpen(true);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  // Initialize scroll position and basic scroll behavior
  useEffect(() => {
    // Ensure page starts at top on initial load/refresh
    const smoothWrapper = document.getElementById('smooth-wrapper');
    if (smoothWrapper) {
      smoothWrapper.scrollTop = 0;
    }

    // Set consistent scroll behavior for all devices
    document.documentElement.style.scrollBehavior = 'auto';
    (document.body.style as any).webkitOverflowScrolling = 'auto';

    return () => {
      // Reset styles on cleanup
      document.documentElement.style.scrollBehavior = 'auto';
      (document.body.style as any).webkitOverflowScrolling = 'auto';
    };
  }, []);

  // Memoize AppRoutes to prevent recreation on every render
  const AppRoutes = useMemo(() => (
    <Routes>
        <Route
          path="/"
          element={
            <div>
              <Home
                onGetStarted={handleGetStarted}
              />
            </div>
          }
        />
        <Route
          path="/iltacon"
          element={
            <div>
              <Home
                onGetStarted={handleGetStarted}
              />
            </div>
          }
        />
        <Route path="/faq" element={<Suspense fallback={<RouteFallback />}><AnimatedPage><FAQPage /></AnimatedPage></Suspense>} />
        <Route path="/about" element={<Suspense fallback={<RouteFallback />}><AnimatedPage><About onGetStarted={handleGetStarted} /></AnimatedPage></Suspense>} />
        <Route path="/events" element={<Suspense fallback={<RouteFallback />}><AnimatedPage><Events onGetStarted={handleGetStarted} /></AnimatedPage></Suspense>} />
        <Route path="/terms" element={<Suspense fallback={<RouteFallback />}><AnimatedPage><TermsConditions /></AnimatedPage></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<RouteFallback />}><AnimatedPage><Privacy /></AnimatedPage></Suspense>} />
        <Route path="/support" element={<Suspense fallback={<RouteFallback />}><AnimatedPage><Support /></AnimatedPage></Suspense>} />
        {FormTestErrors && (
          <Route
            path="/form-test"
            element={
              <Suspense fallback={<RouteFallback />}>
                <AnimatedPage>
                  <FormTestErrors />
                </AnimatedPage>
              </Suspense>
            }
          />
        )}
        <Route path="/maeven-navigator" element={<Suspense fallback={<RouteFallback />}><AnimatedPage><Navigator onGetStarted={handleGetStarted} /></AnimatedPage></Suspense>} />
        <Route path="/platform-terms" element={<Suspense fallback={<RouteFallback />}><AnimatedPage><PlatformTerms /></AnimatedPage></Suspense>} />
        <Route path="/dpa" element={<Suspense fallback={<RouteFallback />}><AnimatedPage><DPA /></AnimatedPage></Suspense>} />
        <Route path="/register" element={<Suspense fallback={<RouteFallback />}><AnimatedPage><Register onGetStarted={handleGetStarted} /></AnimatedPage></Suspense>} />
        {/* Catch-all route - any unmatched URL shows homepage */}
        <Route
          path="*"
          element={
            <div>
              <Home
                onGetStarted={handleGetStarted}
              />
            </div>
          }
        />
      </Routes>
  ), [handleGetStarted]);

  return (
    <ErrorBoundary fallback={<div style={{ padding: '2em', textAlign: 'center' }}>Something went wrong. Please refresh the page.</div>}>
      {/* PerformanceMonitor: Set enabled={true} to track Web Vitals and performance metrics */}
      <PerformanceMonitor enabled={false} logToConsole={false} />
      <GrainOverlay size={2} density={0.2} alpha={20} seed={seed} />
      
      {/* SEO Audit Tool - Remove this after reviewing results 
      <SEOAuditTool />*/}

      {/* simple control (UI = user interface) 
      <button
        onClick={() => setSeed(Math.floor(Math.random() * 1e9))} // new seed → new texture
        style={{ position: "fixed", bottom: 12, left: 12, zIndex: 2147483648 }}
      >
        Regenerate
      </button>*/}
      <HelmetProvider>
      <BrowserRouter>
        <HomePageProvider>
          {/* Section Indicator - Inside Router but outside ScrollSmoother */}
          <HideShowWhatPages />
          <ScrollToTop />
          <div id="smooth-wrapper" ref={main} className="smooth-wrapper bg-transparent">
          <div id="smooth-content" className="smooth-content">
            <div>
              {AppRoutes}
            </div>
            <Footer onGetStarted={handleGetStarted} />
          </div>
        </div>

        <div className="fixed top-0 left-0 w-full z-[1000]">
          <Header onGetStarted={handleGetStarted} />
        </div>

        {sidebarMounted && (
          <Suspense fallback={null}>
            <Sidebar
              isOpen={isSidebarOpen}
              onClose={handleCloseSidebar}
            />
          </Suspense>
        )}

        <LetsTalkModal />
        </HomePageProvider>
      </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;